/**
 * Karnöffel background music — iter M645.
 *
 * Two tracks alternate.  Each plays through ONCE, then the other
 * starts from the beginning.  No internal loop:
 *   1) /sounds/bach-french-suite-2-cm.mp3   (Bach, French Suite No. 2 in C minor)
 *   2) /sounds/sarabande.mp3                (Sarabande — same period)
 *
 * One pre-loaded HTMLAudioElement is reused across mounts so:
 *   • Navigating between Karnöffel sibling pages does NOT restart the
 *     current track — same instance keeps playing.
 *   • Leaving Karnöffel entirely calls stopKarnoffelMusic() which
 *     pauses and rewinds to 0.
 *   • Re-entering Karnöffel resumes from the start of whatever track
 *     was next in the rotation.
 *   • The global mute toggle (`studio:muted` in localStorage) is
 *     honoured.
 */
const TRACK_URLS = [
  "/sounds/bach-french-suite-2-cm.mp3",
  "/sounds/sarabande.mp3",
];
// Per-track volume.  Sarabande is materially louder in its source mp3
// than the Bach, so it is attenuated here (user iter M646).
const TRACK_VOLUMES = [0.5, 0.144];

let _audio = null;
let _trackIdx = 0;

const _applyTrackVolume = () => {
  if (!_audio) return;
  try {
    _audio.volume = TRACK_VOLUMES[_trackIdx] ?? 0.5;
  } catch { /* */ }
};

const isMuted = () => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage.getItem("studio:muted") === "1";
    }
  } catch { /* */ }
  return false;
};

const _advanceTrack = () => {
  if (!_audio) return;
  _trackIdx = (_trackIdx + 1) % TRACK_URLS.length;
  try {
    _audio.src = TRACK_URLS[_trackIdx];
    _audio.currentTime = 0;
  } catch { /* */ }
  _applyTrackVolume();
  if (isMuted()) return;
  try {
    const p = _audio.play();
    if (p && typeof p.then === "function") p.catch(() => { /* */ });
  } catch { /* */ }
};

const getAudio = () => {
  if (typeof window === "undefined") return null;
  if (!_audio) {
    try {
      _audio = new Audio(TRACK_URLS[_trackIdx]);
      _audio.loop = false;        // we manage looping ourselves
      _audio.preload = "auto";
      _applyTrackVolume();
      _audio.addEventListener("ended", _advanceTrack);
    } catch { return null; }
  }
  return _audio;
};

export function startKarnoffelMusic() {
  const a = getAudio();
  if (!a) return;
  if (isMuted()) return;
  if (!a.paused) return;
  try {
    const p = a.play();
    if (p && typeof p.then === "function") p.catch(() => { /* autoplay blocked */ });
  } catch { /* */ }
}

export function stopKarnoffelMusic() {
  if (!_audio) return;
  try {
    _audio.pause();
    _audio.currentTime = 0;
  } catch { /* */ }
}

// Called by the GlobalMuteButton when the user toggles global audio
// on/off.  Pauses the current track immediately; on un-mute we leave
// the audio paused so it only resumes when the user explicitly
// navigates back to a /games/karnoffel page (browser autoplay-gesture
// rule).
export function applyKarnoffelMuteFlag() {
  if (!_audio) return;
  if (isMuted()) {
    try { _audio.pause(); } catch { /* */ }
  } else {
    if (typeof window !== "undefined" &&
        window.location &&
        window.location.pathname.startsWith("/games/karnoffel")) {
      try {
        const p = _audio.play();
        if (p && typeof p.then === "function") p.catch(() => { /* */ });
      } catch { /* */ }
    }
  }
}
